import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetInspectionReportHtmlQuery } from "../../../redux/apiStore";

export function InspectionReportPage() {
  const [searchParams] = useSearchParams();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hasPrintedRef = useRef<boolean>(false);

  const month = useMemo(() => {
    const value = searchParams.get("month");
    return value ? Number(value) : undefined;
  }, [searchParams]);

  const year = useMemo(() => {
    const value = searchParams.get("year");
    return value ? Number(value) : undefined;
  }, [searchParams]);

  const isValidRequest = !!month && !!year;

  const reportHtmlQuery = useGetInspectionReportHtmlQuery(
    {
      month,
      year,
    },
    { skip: !isValidRequest },
  );

  const handlePrint = () => {
    if (iframeRef.current?.contentWindow) {
      iframeRef.current.contentWindow.focus();
      iframeRef.current.contentWindow.print();
      return;
    }

    window.print();
  };

  useEffect(() => {
    hasPrintedRef.current = false;
  }, [month, year]);

  if (!isValidRequest) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">Missing query parameters. Month and year are required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100vh", minHeight: 500, overflow: "hidden", backgroundColor: "#ffffff" }}>
      <Stack sx={{ height: "100%", overflow: "hidden", backgroundColor: "#ffffff" }}>
        {reportHtmlQuery.isFetching && (
          <Stack sx={{ flexGrow: 1 }} alignItems="center" justifyContent="center" gap={1}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading inspection report...
            </Typography>
          </Stack>
        )}

        {!reportHtmlQuery.isFetching && reportHtmlQuery.isError && (
          <Stack sx={{ p: 2 }}>
            <Alert severity="error">Unable to load inspection report HTML.</Alert>
          </Stack>
        )}

        {!reportHtmlQuery.isFetching && !!reportHtmlQuery.data && (
          <iframe
            ref={iframeRef}
            title="Inspection Report"
            srcDoc={reportHtmlQuery.data}
            onLoad={() => {
              if (hasPrintedRef.current) {
                return;
              }

              hasPrintedRef.current = true;
              handlePrint();
            }}
            style={{ width: "100%", height: "100%", border: 0, backgroundColor: "#ffffff" }}
          />
        )}
      </Stack>
    </Box>
  );
}
